import { useCallback, useMemo, useState } from "react";
import type { CoverageAreaDriverEntry } from "store/modules/Logistics/types.store";
import type { DealerUsersProps } from "store/modules/DealerUsers/types.store";
import {
  driverOptionFromEntry,
  DriverOption,
  MAX_DRIVERS_PER_MAP_COVERAGE_AREA,
} from "components/CardDriverArea/coverageAreaDrivers";

export type SlotRow = { key: string; value: DriverOption | null };

function uniqueRowKey(): string {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function useMapAreaDriverSelection(
  maxSlots = MAX_DRIVERS_PER_MAP_COVERAGE_AREA,
) {
  const [slotRows, setSlotRows] = useState<SlotRow[]>([]);

  const resetToDrivers = useCallback(
    (drivers: CoverageAreaDriverEntry[], users: DealerUsersProps[]) => {
      const ordered = [...drivers].sort((a, b) => a.priority - b.priority);
      if (ordered.length === 0) {
        setSlotRows([{ key: uniqueRowKey(), value: null }]);
        return;
      }
      setSlotRows(
        ordered.map((d) => ({
          key: uniqueRowKey(),
          value: driverOptionFromEntry(d, users),
        })),
      );
    },
    [],
  );

  const addEmptySlot = useCallback(() => {
    setSlotRows((prev) => {
      if (prev.length >= maxSlots) return prev;
      return [...prev, { key: uniqueRowKey(), value: null }];
    });
  }, [maxSlots]);

  const setSlotValue = useCallback(
    (key: string, value: DriverOption | null) => {
      setSlotRows((prev) =>
        prev.map((row) => (row.key === key ? { ...row, value } : row)),
      );
    },
    [],
  );

  const removeSlot = useCallback((key: string) => {
    setSlotRows((prev) => {
      const next = prev.filter((row) => row.key !== key);
      if (next.length === 0) {
        return [{ key: uniqueRowKey(), value: null }];
      }
      return next;
    });
  }, []);

  const selectedDriversInOrder = useMemo((): DriverOption[] => {
    return slotRows
      .map((r) => r.value)
      .filter((v): v is DriverOption => v !== null);
  }, [slotRows]);

  const canAddSlot =
    slotRows.length < maxSlots && slotRows.every((row) => row.value !== null);

  return {
    slotRows,
    resetToDrivers,
    addEmptySlot,
    setSlotValue,
    removeSlot,
    selectedDriversInOrder,
    canAddSlot,
  };
}
